import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Minus, ShoppingBag, Check, Sparkles, AlertTriangle, Clock, Mail, Flame, Truck, Star, Package2, Boxes, Send } from "lucide-react";
import axios from "axios";
import { fetchProducts, subscribeNewsletter, API } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useConfig } from "../context/ConfigContext";
import RequestModal from "../components/RequestModal";
import SEO from "../components/SEO";
import { toast } from "sonner";

const ProductCard = ({ product, index, onRequest }) => {
  const { addItem } = useCart();
  const { ecommerceEnabled } = useConfig();
  const [selectedVariant, setSelectedVariant] = useState(() => {
    const v = product.variants || [];
    return v.find((x) => x.popular) || v[0] || null;
  });
  const [qty, setQty] = useState(1);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);

  const available = typeof product.available === "number" ? product.available : product.stock;
  const stockLow = available > 0 && available < 25;
  const outOfStock = available <= 0;
  const comingSoon = !!product.coming_soon;
  const accent = product.accent_color || "#ff1e1e";

  const add = () => {
    if (!selectedVariant) return;
    addItem(
      {
        id: selectedVariant.id,
        name: selectedVariant.label.startsWith(product.name) ? selectedVariant.label : `${product.name} — ${selectedVariant.label}`,
        price: selectedVariant.price,
        units: selectedVariant.units,
        image: product.image,
      },
      qty
    );
    toast.success(`${qty}× ${selectedVariant.label} toegevoegd`);
  };

  const notify = async (e) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifyLoading(true);
    try {
      const res = await subscribeNewsletter(notifyEmail);
      toast.success(res.message || "Je krijgt 'm als eerste!");
      setNotifyEmail("");
    } catch {
      toast.error("Aanmelden mislukt.");
    } finally {
      setNotifyLoading(false);
    }
  };

  const num = String(index + 1).padStart(3, "0");

  return (
    <section
      data-testid={`product-card-${product.id}`}
      className="py-20 border-t border-[var(--tha-border)]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div
            className="relative aspect-square border border-[var(--tha-border)] bg-black overflow-hidden"
            style={{
              boxShadow: comingSoon
                ? `0 0 0 1px ${accent}66, 0 0 28px ${accent}44, 0 0 80px ${accent}22`
                : `0 0 0 1px ${accent}99, 0 0 28px ${accent}66, 0 0 80px ${accent}33`,
            }}
          >
            <img
              src={product.image}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`product-image-${product.id}`}
            />
            {product.badge && (
              <div
                data-testid={`product-badge-${product.id}`}
                className="absolute top-5 right-5 px-4 py-2 font-display text-2xl tracking-wider rotate-[-6deg] shadow-2xl"
                style={{
                  background: accent,
                  color: "#050505",
                  boxShadow: `0 8px 24px ${accent}66, 0 0 0 2px #050505`,
                }}
              >
                {product.badge}
              </div>
            )}
            <div className="absolute top-5 left-5 text-[10px] font-mono-x tracking-widest text-white/80">
              #{num} / {product.volume.toUpperCase()}
            </div>
            {!comingSoon && stockLow && (
              <div
                data-testid={`stock-badge-${product.id}`}
                className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1.5 border text-[10px] font-mono-x tracking-[0.25em] animate-pulse"
                style={{
                  borderColor: accent,
                  color: accent,
                  background: "rgba(5,5,5,0.85)",
                  boxShadow: `0 0 18px ${accent}66`,
                }}
              >
                <Flame size={11} /> NOG {available} OVER
              </div>
            )}
            {comingSoon && (
              <div
                data-testid={`coming-soon-overlay-${product.id}`}
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  background:
                    "radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 60%, rgba(0,0,0,0.15) 100%)",
                }}
              >
                <div className="text-center px-6">
                  <div
                    className="inline-flex items-center gap-2 text-[10px] font-mono-x tracking-[0.3em]"
                    style={{ color: accent, textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}
                  >
                    <Clock size={12} /> BINNENKORT
                  </div>
                  <div
                    className="font-display text-5xl sm:text-6xl text-white mt-3 leading-none"
                    style={{
                      textShadow:
                        "0 4px 24px rgba(0,0,0,0.95), 0 2px 8px rgba(0,0,0,0.9)",
                    }}
                  >
                    COMING <span style={{ color: accent }}>SOON</span>
                  </div>
                </div>
              </div>
            )}
            {!comingSoon && outOfStock && (
              <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                <div className="font-display text-5xl" style={{ color: accent }}>
                  UITVERKOCHT
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div
            className="text-xs font-mono-x tracking-[0.3em]"
            style={{ color: accent }}
          >
            {index === 0 ? "NIEUW BIJ TIK HEM AAN!" : `SHOT #${num}`}
          </div>
          <h2
            data-testid={`product-name-${product.id}`}
            className="font-display text-5xl sm:text-7xl mt-4 leading-none"
          >
            {product.name.toUpperCase()}
          </h2>
          <div className="text-[var(--tha-muted)] mt-3 italic">{product.tagline}</div>

          {product.review_count > 0 && (
            <div
              data-testid={`product-rating-${product.id}`}
              className="mt-4 inline-flex items-center gap-2"
            >
              <StarRow rating={Math.round(product.review_avg)} accent={accent} />
              <span className="text-xs font-mono-x text-[var(--tha-muted)]">
                {product.review_avg.toFixed(1)} · {product.review_count} review{product.review_count === 1 ? "" : "s"}
              </span>
            </div>
          )}

          <p className="mt-8 text-[var(--tha-muted)] leading-relaxed max-w-lg">
            {product.description}
          </p>

          <div className="mt-8 grid grid-cols-3 gap-2 max-w-sm">
            <Stat label="ALC" value={product.alcohol.replace(" vol", "")} />
            <Stat label="INHOUD" value={product.volume.toUpperCase()} />
            <Stat label="SERVE" value="ICE" />
          </div>

          {comingSoon ? (
            <div
              data-testid={`coming-soon-block-${product.id}`}
              className="mt-10 border p-6"
              style={{ borderColor: accent, background: `${accent}11` }}
            >
              <div
                className="flex items-center gap-2 text-xs font-mono-x tracking-[0.3em]"
                style={{ color: accent }}
              >
                <Clock size={14} /> BINNENKORT VERKRIJGBAAR
              </div>
              <div className="font-display text-3xl sm:text-4xl text-white mt-3 leading-none">
                NOG EVEN GEDULD.
              </div>
              <p className="mt-4 text-sm text-[var(--tha-muted)]">
                {product.name} is bijna klaar. Meld je aan en je krijgt 'm als eerste in je inbox.
              </p>
              <form
                onSubmit={notify}
                data-testid={`notify-form-${product.id}`}
                className="mt-5 flex flex-col sm:flex-row gap-0"
              >
                <input
                  data-testid={`notify-email-input-${product.id}`}
                  required
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder="jouw@email.nl"
                  className="flex-1 px-4 py-3 text-sm"
                />
                <button
                  data-testid={`notify-submit-${product.id}`}
                  disabled={notifyLoading}
                  className="px-6 py-3 font-display tracking-wider text-sm disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-colors"
                  style={{ background: accent, color: "#050505" }}
                >
                  <Mail size={14} /> {notifyLoading ? "..." : "WAARSCHUW MIJ"}
                </button>
              </form>
            </div>
          ) : (
            <>
              <div className="mt-10">
                <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] mb-3">
                  KIES JE FORMAAT
                </div>
                <div className="space-y-2" data-testid={`variant-selector-${product.id}`}>
                  {product.variants.map((v) => {
                    const isSelected = selectedVariant?.id === v.id;
                    const perUnit = (v.price / v.units).toFixed(2);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        data-testid={`variant-${v.id}`}
                        onClick={() => setSelectedVariant(v)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-4 border text-left transition-colors ${
                          isSelected
                            ? "bg-[#1a0303]"
                            : "border-[var(--tha-border)] bg-[#0a0a0a] hover:border-white"
                        }`}
                        style={isSelected ? { borderColor: accent, background: `${accent}1a` } : {}}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full border"
                            style={
                              isSelected
                                ? { background: accent, borderColor: accent }
                                : { borderColor: "var(--tha-muted)" }
                            }
                          />
                          <div>
                            <div className="font-display text-base sm:text-lg text-white flex items-center gap-2">
                              {v.label}
                              {v.popular && (
                                <span
                                  className="inline-flex items-center gap-1 text-[9px] font-mono-x tracking-widest px-2 py-0.5"
                                  style={{ background: accent, color: "#000" }}
                                >
                                  <Sparkles size={9} /> POPULAIR
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] font-mono-x text-[var(--tha-muted)] mt-0.5">
                              {v.units} {product.product_type === "box" ? (v.units === 1 ? "box" : "boxen") : v.units === 1 ? "flesje" : "flesjes"}
                              {ecommerceEnabled ? ` · € ${perUnit} per shot` : ""}
                            </div>
                          </div>
                        </div>
                        {ecommerceEnabled ? (
                          <div className="font-display text-xl text-white">
                            € {v.price.toFixed(2)}
                          </div>
                        ) : (
                          <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
                            OP AANVRAAG
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {stockLow && !outOfStock && (
                <div
                  className="mt-5 flex items-center gap-2 text-xs font-mono-x tracking-widest"
                  style={{ color: accent }}
                >
                  <AlertTriangle size={14} /> NOG MAAR {available} FLESJES OP VOORRAAD
                </div>
              )}

              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center border border-[var(--tha-border)]">
                  <button
                    data-testid={`shop-qty-decrease-${product.id}`}
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="p-3 hover:bg-[var(--tha-surface)]"
                    aria-label="Min"
                  >
                    <Minus size={14} />
                  </button>
                  <span className="px-5 font-mono-x text-sm min-w-[40px] text-center">
                    {qty}
                  </span>
                  <button
                    data-testid={`shop-qty-increase-${product.id}`}
                    onClick={() => setQty((q) => q + 1)}
                    className="p-3 hover:bg-[var(--tha-surface)]"
                    aria-label="Plus"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {ecommerceEnabled ? (
                  <button
                    data-testid={`shop-add-to-cart-${product.id}`}
                    onClick={add}
                    disabled={outOfStock}
                    className="flex-1 py-4 font-display tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    style={{ background: accent, color: "#050505" }}
                  >
                    <ShoppingBag size={16} /> {outOfStock ? "UITVERKOCHT" : "IN MANDJE"}
                  </button>
                ) : (
                  <button
                    data-testid={`shop-request-${product.id}`}
                    onClick={() => onRequest?.(product, selectedVariant, qty)}
                    className="flex-1 py-4 font-display tracking-wider inline-flex items-center justify-center gap-2 transition-colors"
                    style={{ background: accent, color: "#050505" }}
                  >
                    <Send size={16} /> BESTELLING AANVRAGEN
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") === "box" ? "box" : "shot");
  const [reviews, setReviews] = useState([]);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqProduct, setReqProduct] = useState(null);
  const [reqVariant, setReqVariant] = useState(null);
  const { ecommerceEnabled } = useConfig();

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => toast.error("Producten laden mislukt"))
      .finally(() => setLoading(false));
    axios.get(`${API}/reviews?limit=12`).then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);

  const openRequest = (product, variant) => {
    setReqProduct(product);
    setReqVariant(variant);
    setReqOpen(true);
  };

  // Orderable products (not coming_soon) first, then by order
  const sortFn = (a, b) =>
    (a.coming_soon ? 1 : 0) - (b.coming_soon ? 1 : 0) ||
    (a.order || 999) - (b.order || 999);

  const shots = useMemo(
    () => products.filter((p) => (p.product_type || "shot") === "shot").sort(sortFn),
    [products]
  );
  const boxes = useMemo(
    () => products.filter((p) => p.product_type === "box").sort(sortFn),
    [products]
  );

  const visible = tab === "shot" ? shots : boxes;

  return (
    <div data-testid="shop-page" className="relative">
      <SEO
        title="Shop · De volledige line-up"
        description="Ontdek alle Tik Hem Aan! shots: Red Rush, Electric Energy, Dark Drop en de proef- en combo-boxen. Vraag je bestelling aan — reactie binnen 1 werkdag."
        path="/shop"
      />
      <section className="py-20 border-b border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              SHOP — {shots.length} SHOTS · {boxes.length} DOZEN
            </span>
          </div>
          <h1 className="font-display text-6xl sm:text-8xl mt-5 leading-none">
            DE <span className="neon-red-text">DROP.</span>
          </h1>
          <p className="mt-5 text-[var(--tha-muted)] max-w-xl">
            De volledige line-up. Eén familie, meerdere smaken — elk met z'n eigen tik.
          </p>
        </div>
      </section>

      {/* Tab strip */}
      <div className="border-b border-[var(--tha-border)] bg-[#0a0a0a] sticky top-[64px] z-[5]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 flex">
          <TabButton
            active={tab === "shot"}
            onClick={() => setTab("shot")}
            testId="shop-tab-shots"
            icon={Package2}
            label="LOSSE SHOTJES"
            count={shots.length}
          />
          <TabButton
            active={tab === "box"}
            onClick={() => setTab("box")}
            testId="shop-tab-boxes"
            icon={Boxes}
            label="SHOTDOZEN"
            count={boxes.length}
          />
        </div>
      </div>

      {/* Free shipping advert bar — only when ecommerce is enabled */}
      {ecommerceEnabled && (
        <div
          data-testid="free-shipping-bar"
          className="border-b border-[var(--tha-border)] bg-[#0a0a0a]"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
            <div className="inline-flex items-center gap-2 text-white">
              <Truck size={16} className="text-[var(--tha-red)]" />
              <span className="font-mono-x tracking-widest">
                DHL — € 5,95 PER PAKKET
              </span>
            </div>
            <span className="hidden sm:inline text-[var(--tha-muted)]">·</span>
            <div className="inline-flex items-center gap-2">
              <Sparkles size={14} className="text-[var(--tha-red)]" />
              <span className="font-display text-base sm:text-lg neon-red-text">
                GRATIS VERZENDING VANAF € 60
              </span>
            </div>
          </div>
        </div>
      )}

      {!ecommerceEnabled && (
        <div
          data-testid="request-mode-banner"
          className="border-b border-[var(--tha-border)] bg-[#0a0a0a]"
        >
          <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
            <div className="inline-flex items-center gap-2 text-white">
              <Send size={14} className="text-[var(--tha-red)]" />
              <span className="font-mono-x tracking-widest">
                BESTELLING OP AANVRAAG
              </span>
            </div>
            <span className="hidden sm:inline text-[var(--tha-muted)]">·</span>
            <div className="inline-flex items-center gap-2">
              <span className="font-display text-base sm:text-lg text-white">
                REACTIE BINNEN <span className="neon-red-text">1 WERKDAG</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-32 text-center text-[var(--tha-muted)]">
          <div className="font-display text-3xl">LADEN...</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="py-32 text-center text-[var(--tha-muted)] font-mono-x">
          {tab === "box"
            ? "Nog geen shotdozen beschikbaar."
            : "Geen losse shotjes gevonden."}
        </div>
      ) : (
        visible.map((p, idx) => <ProductCard key={p.id} product={p} index={idx} onRequest={openRequest} />)
      )}

      {reviews.length > 0 && <ReviewsSection reviews={reviews} />}

      <RequestModal
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        product={reqProduct}
        variant={reqVariant}
      />
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label, count, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`relative px-5 sm:px-7 py-5 inline-flex items-center gap-3 transition-colors ${
      active
        ? "text-white"
        : "text-[var(--tha-muted)] hover:text-white"
    }`}
  >
    <Icon size={16} />
    <span className="font-display text-sm sm:text-base tracking-widest">{label}</span>
    <span
      className={`text-[10px] font-mono-x tracking-widest px-1.5 py-0.5 border ${
        active ? "border-[var(--tha-red)] text-[var(--tha-red)]" : "border-[var(--tha-border)] text-[var(--tha-muted)]"
      }`}
    >
      {count}
    </span>
    {active && (
      <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-[var(--tha-red)]" />
    )}
  </button>
);

const StarRow = ({ rating, size = 14, accent = "#ff1e1e" }) => (
  <div className="inline-flex gap-0.5" aria-label={`${rating} sterren`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        size={size}
        strokeWidth={1.5}
        fill={n <= rating ? accent : "transparent"}
        color={n <= rating ? accent : "#3f3f3f"}
      />
    ))}
  </div>
);

const ReviewsSection = ({ reviews }) => {
  return (
    <section
      data-testid="reviews-section"
      className="py-20 border-t border-[var(--tha-border)] bg-[#080808]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-12 bg-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
            ECHTE STEMMEN · {reviews.length} REVIEWS
          </span>
        </div>
        <h2 className="font-display text-5xl sm:text-6xl leading-none mb-12">
          WAT <span className="neon-red-text">ZIJ</span> ZEGGEN.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--tha-border)]">
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={`review-${r.id}`}
              className="bg-[#0a0a0a] p-6 flex flex-col"
            >
              <StarRow rating={r.rating} />
              {r.title && (
                <h3 className="font-display text-xl mt-3 leading-tight">
                  {r.title}
                </h3>
              )}
              <p className="mt-3 text-sm text-[var(--tha-muted)] flex-1 whitespace-pre-line">
                {r.body}
              </p>
              {r.photo_urls && r.photo_urls.length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-1">
                  {r.photo_urls.map((u, i) => (
                    <a
                      key={i}
                      href={u}
                      target="_blank"
                      rel="noreferrer"
                      className="block aspect-square overflow-hidden border border-[var(--tha-border)] hover:border-[var(--tha-red)] transition-colors"
                    >
                      <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-[var(--tha-border)] flex items-center justify-between text-xs font-mono-x">
                <span className="text-white">{r.display_name || "Anoniem"}</span>
                <span className="text-[var(--tha-muted)]">
                  {(r.product_names || []).join(" · ")}
                </span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => {
  const parts = String(value).split(" · ");
  return (
    <div className="border border-[var(--tha-border)] p-3">
      <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">{label}</div>
      {parts.length > 1 ? (
        <div className="mt-1 space-y-0.5">
          {parts.map((t) => (
            <div key={t} className="text-[10px] font-mono-x leading-snug text-white">{t}</div>
          ))}
        </div>
      ) : (
        <div className="font-display text-xl mt-1">{value}</div>
      )}
    </div>
  );
};

export default Shop;
